import { useCallback, useEffect, useState } from "react";
import { API_BASE_URLS } from "../../constants/api";
import { apiUtils } from "../../utils";
import type {
  QuestionnaireHistoryItem,
  QuestionnaireSectionApi,
  QuestionnaireSectionEntry
} from "./questionnaire.types";

interface UseQuestionnaireApiProps {
  patientUserId?: string;
  formGroup: string;
}

export function useQuestionnaireApi({ patientUserId, formGroup }: UseQuestionnaireApiProps) {
  const [sections, setSections] = useState<
    QuestionnaireSectionEntry[]
  >([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [history, setHistory] = useState<QuestionnaireHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const fetchSections = useCallback(async () => {
    setSectionsLoading(true);
    setSectionsError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("GET");
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/questionnaires/sections?formGroup=${formGroup}`,
        requestOptions
      );
      const result = await apiUtils.handleResponse<{ sections: QuestionnaireSectionApi[] }>(response);
      const rawSections = Array.isArray(result?.sections) ? result.sections : [];
      const mapped = rawSections.map((item) => ({
        questionnaireId: item.questionnaireId,
        isOptionalSection: Boolean(item.isOptionalSection),
        sectionGroupLabel: item.sectionGroupLabel,
        section: {
          sectionId: item.sectionId || item.questionnaireId,
          title: item.sectionTitle || item.questionnaireId,
          questions: (item.steps || []).map((step) => ({
            questionId: step.questionId,
            label: step.label,
            type: (step.type as any) || "text",
            required: step.required,
            helperText: step.helperText,
            placeholder: step.placeholder,
            unit: step.unit,
            options: (step.options || []).map((option) => ({
              label: option.label,
              value: option.value,
              isExclusive: option.isExclusive,
              meta: option.meta
            })),
            showWhen: step.showWhen || []
          }))
        }
      }));
      setSections(mapped);
      return mapped;
    } catch (err: any) {
      setSectionsError(err?.message || "Failed to load questionnaire sections.");
      setSections([]);
      return [];
    } finally {
      setSectionsLoading(false);
    }
  }, [formGroup]);

  const fetchQuestionnaireHistory = useCallback(async () => {
    if (!patientUserId) return [] as QuestionnaireHistoryItem[];
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("GET");
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/patients/${patientUserId}/questionnaires?formGroup=${formGroup}`,
        requestOptions
      );
      const result = await apiUtils.handleResponse<any>(response);
      const submissions = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : [];
      setHistory(submissions);
      return submissions as QuestionnaireHistoryItem[];
    } catch (err: any) {
      setHistoryError(err?.message || "Failed to load questionnaire history.");
      return [] as QuestionnaireHistoryItem[];
    } finally {
      setHistoryLoading(false);
    }
  }, [formGroup, patientUserId]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  useEffect(() => {
    fetchQuestionnaireHistory();
  }, [fetchQuestionnaireHistory]);

  return {
    sections,
    sectionsLoading,
    sectionsError,
    history,
    historyLoading,
    historyError,
    fetchSections,
    fetchQuestionnaireHistory
  };
}
